import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Pause, Play, RotateCcw, Sparkles } from "lucide-react";
import { cueStillFor, stepCue, CueStill } from "../live/cueRows";
import { LiveAngleChart } from "./LiveAngleChart";
import type { MetricChartData } from "../api/types";
import {
  LivePoseFile,
  DwellMoment,
  framePoints,
  frameIndexAt,
  interpolatedFramePoints,
  alignPoseFileToFrameList,
  playbackGeometry,
  placementStyle,
  cropStyle,
  StageGeometry,
  SKELETON_BONES,
  LiveSeries,
  sampleAtIndex,
  seriesChart,
  athleteCrop,
  zoomedGeometry,
  isFullFrameCrop,
  captureFpsFor,
  liveSeriesOnCadence,
  activeStrikePulses,
  ribbonOutline,
  strideSeconds,
  pulseRing,
} from "../live/liveAnalysis";

/**
 * The LIVE free-analysis view (user reference clip, 2026-09-12): the original upload autoplays
 * with the skeleton drawn from the client-pose pose file; crossing a feedback moment can pause
 * the clip and fade that position's still in (a persisted preference — "pause or play straight
 * through" was the user's call to make, not ours); for run gait a live panel accumulates
 * cadence and knee-at-strike using the SAME chart card the session page renders at the bottom
 * (LiveAngleChart), so the live view and the metrics section read as one platform.
 *
 * Skeleton smoothness: keypoints are 10 fps but the clip renders at native rate, so each
 * landmark is interpolated between the bracketing pose file frames (../live/liveAnalysis,
 * tested) instead of stepping once per split frame — the jitter fix.
 */
/** One coaching row — the page's own rows (an overlay's headline text on a P-position frame). */
export interface LiveCoachingRow {
  pLabel: string;
  fileId: number;
  /** The cue: the overlay class (the renderer's segmentKey). */
  overlay: string;
  description: string;
  subText?: string;
  /** `${pLabel}:${overlay}` — one cue on one position (live/cueRows). */
  key?: string;
  /** The cue's readout cards ("Shaft 52°"), shown on its row. */
  values?: string[];
}

export interface LiveCoaching {
  summary?: string | null;
  total?: string | null;
  rows: LiveCoachingRow[];
}

export interface LiveFreeAnalysisProps {
  clipUrl: string;
  poseFile: LivePoseFile;
  moments: DwellMoment[];
  activity: string;
  /** Prefix for the moments' relative stillUrl values (the API base). */
  stillBase: string;
  /** Bottom-right stamp, the role the frame id plays on the rendered stills. */
  watermarkStamp?: string;
  /** The page's existing metric charts (the ones at the bottom of the session page) — shown
   * beside the player so the live view and the metrics section are one surface. */
  pageCharts?: MetricChartData[];
  /** The renderer's published placement + crop for this upload's stills (freeFeedback.stage).
   * Absent → the sport's known canvas, uncropped (older uploads look exactly as before). */
  stageGeometry?: StageGeometry | null;
  /** The page's coaching feedback (summary, total, rows) — shown under a Coaching tab beside
   * the Metrics tab (user, 2026-09-12: "merge the coaching feedback together with the charts
   * and have tab choices at the top"). */
  coaching?: LiveCoaching | null;
  /** The split window (freeFeedback.split): the pose file's time base. Frame i plays at
   * startSec + i/fps, and playback loops inside [startSec, min(stopSec, startSec + n/fps)) —
   * outside it there is no pose to draw (user, 2026-09-12: "near the end the pose lags").
   * `fps` is the upload's STORED capture cadence (the splitter's value) and beats the pose file's
   * own stamp; absent = the pose file's, then the legacy 10. */
  splitWindow?: { startSec: number; stopSec: number; fps?: number } | null;
  /** The upload's ordered split-frame ids (uploadFrames): a frame-keyed pose file is laid out over
   * them so frame i is split frame i even when the pose file skipped some. */
  orderedFrameIds?: number[];
  /** The C# analysis pass's live-series artifact (GET /uploads/liveSeries). Null = not produced
   * yet (404): the live metric panels hide; NOTHING is computed here instead. */
  liveSeries?: LiveSeries | null;
  /** One still per cue (freeFeedback.cueStills): the stage shows the selected cue's own picture, the
   * way the Pro video shows each cue on its own. Empty for uploads rendered before cue stills existed —
   * the stage then shows the position's composite, labelled as such. */
  cueStills?: CueStill[] | null;
  /** How long the clip holds on EACH cue when it pauses at a position by itself. */
  dwellMs?: number;
}

// Platform palette (tailwind.config.js): the right side is the primary green and the left the
// accent violet — bones, joints, ankle trails, strike rings, joint series and tile eyebrows alike
// (user, 2026-09-12: "we should use better matching platform colors", not orange + blue;
// 2026-09-19: colour the pose per leg like the reference clip).
// Cross-body bones (the shoulders–hips box): neutral, so the two sides' colours carry the reading.
const TORSO = "rgba(255,255,255,0.75)";
const JOINT = "#ffffff";
const TRAIL_LEFT = "#9b7fe8"; // accent.violet
const TRAIL_RIGHT = "#10b981"; // el-500
/** The gait look the owner picked from the side-by-side (2026-09-19): the stride ribbon, at 1.15.
 * Strength multiplies the ribbon's width and glow, and nothing else — the strike pulse was already
 * settled and stays as it was (2026-09-20: "i prefer the older pulses"). PROVISIONAL — a look,
 * chosen by eye against upload 17820. */
const LOOK_STRENGTH = 1.15;
const TRAIL_HEAD_WIDTH = 26; // capture px at the foot, tapering to a point at the tail
const TRAIL_TAPER = 1.25;
const TRAIL_GLOW = 26;
const PULSE_CORE_RADIUS = 8; // the strike blob, in capture pixels

/** A bone's colour: both ends on one side take that side's colour; anything else (the
 * shoulders–hips box, cross-body bones) stays the neutral bone colour. */
function sideColor(a: string, b: string): string {
  if (a.startsWith("left_") && b.startsWith("left_")) return TRAIL_LEFT;
  if (a.startsWith("right_") && b.startsWith("right_")) return TRAIL_RIGHT;
  return TORSO;
}

const AUTO_PAUSE_KEY = "eleetz.liveFree.autoPause";
const ZOOM_KEY = "eleetz.liveFree.zoom";
// The panel (numbers, chart cursors) re-renders at a fixed 10 Hz whatever the upload's cadence:
// a component-wide React re-render per pose frame would not keep up at 60 fps, and the canvas
// (which does follow every frame) never waits for it.
const PANEL_TICK_HZ = 10;
// The ankle trail is a fixed span of TIME, so it covers the same stretch of running at any cadence.
const TRAIL_SECONDS = 4;

/** The renderer's watermark, replicated from BaseSetFinder.DrawBrandText so playback and the
 * burnt-in marks on the stills read as ONE surface: center wordmark 140px at 0x60 alpha, the
 * 45°-rotated pinstripe tile (34px, 0x0E alpha, 360/120 grid, brick offset), and the
 * bottom-right stamp (24px, 0x70 alpha, 60/20 px padding). SVG viewBox 1280x720 scales with
 * the stage exactly as the server's scaleFactor does. Hidden while a still shows — the still
 * carries its own marks. */
function RendererWatermark({ stamp, w, h }: { stamp?: string; w: number; h: number }) {
  // The server scales every size by min(w/1280, h/720) — DrawBrandText's scaleFactor.
  const k = Math.min(w / 1280, h / 720);
  const cx = w / 2;
  const cy = h / 2;
  const tiles: React.ReactNode[] = [];
  const diag = Math.sqrt(w * w + h * h);
  const stepX = 360 * k;
  const stepY = 120 * k;
  let row = 0;
  for (let y = cy - diag / 2; y <= cy + diag / 2; y += stepY, row++) {
    const rowOffset = row % 2 === 0 ? 0 : stepX / 2;
    for (let x = cx - diag / 2; x <= cx + diag / 2; x += stepX)
      tiles.push(
        <text key={`${row}-${x}`} x={x + rowOffset} y={y} fontSize={34 * k} textAnchor="middle">
          Eleetz.com
        </text>
      );
  }
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="absolute inset-0 w-full h-full pointer-events-none"
      data-testid="live-watermark"
      fill="#ffffff"
      fontFamily="Arial, sans-serif"
      fontWeight="bold"
    >
      <g opacity={0x0e / 255} transform={`rotate(-45 ${cx} ${cy})`}>{tiles}</g>
      <text x={cx} y={cy} fontSize={140 * k} textAnchor="middle" dominantBaseline="middle" opacity={0x60 / 255}>
        Eleetz.com
      </text>
      {stamp && (
        <text x={w - 60 * k} y={h - 20 * k} fontSize={24 * k} textAnchor="middle" opacity={0x70 / 255}>
          {stamp}
        </text>
      )}
    </svg>
  );
}

/** A compact metric tile in the platform's card language (white card, slate eyebrow) — the
 * reference product's tile grid, our palette. */
function Tile({ value, label, testid }: { value: string; label: string; testid?: string }) {
  return (
    <div className="rounded-md bg-white border border-slate-100 px-3 py-2 min-w-0" data-testid={testid}>
      <div className="text-lg font-semibold text-slate-900 leading-tight tracking-tightish truncate">{value}</div>
      <div className="text-[0.6rem] uppercase tracking-[0.12em] text-slate-500 mt-0.5 truncate">{label}</div>
    </div>
  );
}

// Stills are click-enabled (user, 2026-09-12): auto-pausing on feedback is opt-in now.
type AngleSampleList = LiveSeries["kneeAtStrike"] extends infer K ? (K extends { perStrike: infer P } ? P : never) : never;

function loadAutoPause(): boolean {
  try {
    return window.localStorage.getItem(AUTO_PAUSE_KEY) === "on";
  } catch {
    return false;
  }
}

function loadZoom(): boolean {
  try {
    return window.localStorage.getItem(ZOOM_KEY) !== "off";
  } catch {
    return true;
  }
}

export default function LiveFreeAnalysis({
  clipUrl,
  poseFile,
  moments,
  activity,
  stillBase,
  watermarkStamp,
  pageCharts = [],
  stageGeometry = null,
  coaching = null,
  splitWindow = null,
  orderedFrameIds,
  liveSeries = null,
  cueStills = null,
  dwellMs = 2600,
}: LiveFreeAnalysisProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const backdropRef = useRef<HTMLVideoElement | null>(null);
  const lastTimeRef = useRef(0);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const progressRef = useRef<HTMLDivElement | null>(null);
  const consumedRef = useRef<Set<string>>(new Set());
  const dwellTimerRef = useRef<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [dwelling, setDwelling] = useState<DwellMoment | null>(null);
  const [autoPause, setAutoPause] = useState<boolean>(loadAutoPause);
  const [zoom, setZoom] = useState<boolean>(loadZoom);
  const [panelTick, setPanelTick] = useState(0); // panel refresh at the pose file rate while playing
  const [seriesPick, setSeriesPick] = useState<string | null>(null); // the big chart's series
  const [pickedTab, setPickedTab] = useState<"coaching" | "metrics" | null>(null);
  // The ONE selected cue (a row key). Never a position: selecting by position is what lit every row of
  // P1 at once and showed them all as one picture (owner, 2026-09-25).
  const [activeCue, setActiveCue] = useState<string | null>(null);
  const coachingRows = useMemo(
    () => (coaching?.rows ?? []).map((r) => ({ ...r, key: r.key ?? `${r.pLabel}:${r.overlay}` })),
    [coaching?.rows]
  );
  const cueKeysAt = (label: string) => coachingRows.filter((r) => r.pLabel === label).map((r) => r.key);
  const cueKeysAtRef = useRef(cueKeysAt);
  cueKeysAtRef.current = cueKeysAt;
  const activeCueRef = useRef(activeCue);
  activeCueRef.current = activeCue;
  // What the stage shows while held: the selected cue's OWN still; if this upload has none for it (rendered
  // before cue stills existed), the position's composite — labelled, so it is never mistaken for one cue.
  const activeRowData = activeCue ? coachingRows.find((r) => r.key === activeCue) : undefined;
  const activeCueStill =
    dwelling && activeRowData && activeRowData.pLabel === dwelling.label
      ? cueStillFor(cueStills, activeRowData.pLabel, activeRowData.overlay)
      : null;
  const stageStillUrl = activeCueStill ?? dwelling?.stillUrl ?? null;
  const showingAllCues = !!dwelling && !activeCueStill && !!activeRowData;
  // Coaching opens first when there is any; rows can arrive after mount, so the default is
  // derived until the user picks a tab.
  const tab: "coaching" | "metrics" = pickedTab ?? (coachingRows.length > 0 ? "coaching" : "metrics");
  // The draw loop reads the current tab without re-subscribing: the 10 Hz panel tick (a
  // component-wide React re-render) runs only while the Metrics tab consumes it, so it never
  // competes with the canvas on the main thread while the coaching list is up.
  const tabRef = useRef(tab);
  tabRef.current = tab;

  const fps = captureFpsFor(splitWindow, poseFile);
  // The artifact is only shown when it was computed at THIS cadence: at another one its numbers
  // are wrong (cadence, timings), so a stale one is hidden behind a note, never displayed.
  const series = liveSeriesOnCadence(liveSeries, fps) ? liveSeries : null;
  // The strikes as split-frame indices for the draw loop (a ref: the loop never re-subscribes).
  const strikeIdxRef = useRef<{ left: number[]; right: number[] }>({ left: [], right: [] });
  strikeIdxRef.current = series
    ? { right: series.strikes.map((f) => f - series.minFrame), left: (series.leftStrikes ?? []).map((f) => f - series.minFrame) }
    : { left: [], right: [] };
  const aligned = useMemo(
    () => (orderedFrameIds && orderedFrameIds.length > 0 ? alignPoseFileToFrameList(poseFile, orderedFrameIds) : poseFile),
    [poseFile, orderedFrameIds]
  );
  const frames = useMemo(() => aligned.frames.map(framePoints), [aligned]);
  // The pose file's time base: frame i ↔ spanStart + i/fps; the analysed span ends at the last
  // frame that HAS a pose (or the window's end), whichever is first — never past the clip.
  const spanStart = Math.max(0, splitWindow?.startSec ?? 0);
  const posedFrames = useMemo(() => {
    let last = -1;
    frames.forEach((f, i) => {
      if (f.size > 0) last = i;
    });
    return last + 1;
  }, [frames]);
  const spanEndFor = (duration: number) =>
    Math.min(splitWindow?.stopSec ?? Infinity, spanStart + posedFrames / fps, duration > 0 ? duration : Infinity);
  const relTime = (t: number) => Math.max(0, t - spanStart);
  const isGait = activity === "runGait";

  // ONE geometry across free and premium (user decision, 2026-09-12): the clip plays exactly
  // where the renderer placed it, cropped exactly as the renderer cropped it, on the canvas the
  // stills were composed on — the PUBLISHED stage when this upload has one (the body-framing
  // crop that makes the runner fill the frame), else the sport's known canvas and the ported
  // placement math, uncropped (../live/liveAnalysis, tested).
  const geometry = useMemo(
    () => playbackGeometry(activity, poseFile.captureWidth || 720, poseFile.captureHeight || 1280, stageGeometry),
    [poseFile, activity, stageGeometry]
  );
  const stage = geometry.canvas;
  // Athlete zoom (user, 2026-09-12: "zoom the videos if the keypoints are smallish"): when the
  // renderer published no crop, the clip is framed on the keypoints' bounding box with the
  // renderer's own rule (../live/liveAnalysis athleteCrop). A published crop already frames the
  // athlete, so it wins and no client zoom is offered. Stills stay the full composition on purpose.
  const serverCropped = !isFullFrameCrop(geometry.crop);
  const captureW = poseFile.captureWidth || 720;
  const captureH = poseFile.captureHeight || 1280;
  const clientCrop = useMemo(
    () => (serverCropped ? null : athleteCrop(frames, captureW, captureH, stage.w, stage.h)),
    [serverCropped, frames, captureW, captureH, stage]
  );
  const effective = zoom && clientCrop ? zoomedGeometry(geometry, clientCrop, captureW, captureH) : geometry;
  const windowStyle = placementStyle(effective.placement, stage);
  const clipStyle = cropStyle(effective.crop);

  // The stage's on-screen size, measured and set in PIXELS. CSS aspect-ratio with w-full +
  // max-h-full silently breaks the ratio in a wide container (the browser keeps the forced
  // width and clamps the height) — which is exactly how the still and the clip ended up in
  // different boxes on 17816. Contain-fit the stage into the panel ourselves; no negotiation.
  const [stageBox, setStageBox] = useState<{ w: number; h: number } | null>(null);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const fit = () => {
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      if (cw <= 0 || ch <= 0) return;
      const k = Math.min(cw / stage.w, ch / stage.h);
      setStageBox({ w: Math.round(stage.w * k), h: Math.round(stage.h * k) });
    };
    fit();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [stage]);


  // The draw loop. State is NOT touched per frame (that re-rendered the whole component at
  // display rate — the other half of the jitter): the progress bar is written straight to the
  // DOM, and the panel refreshes at the pose file rate through a value that changes 10× a second.
  useEffect(() => {
    let raf = 0;
    // The clock (user, 2026-09-19: "the pose looks a bit behind the motion"). The split frames
    // pair exactly with the clip (frame 20 = 2.00 s, measured), so the lag was currentTime, which
    // trails the frame actually on screen. Where the browser offers it, the presented frame's own
    // mediaTime (requestVideoFrameCallback) is the time the pose is drawn at, advanced by the time
    // since that frame was painted; elsewhere, and while paused, currentTime as before.
    const video0 = videoRef.current;
    let presented: { mediaTime: number; at: number } | null = null;
    let vfc = 0;
    const withVfc = video0 as (HTMLVideoElement & { requestVideoFrameCallback?: (cb: (now: number, meta: { mediaTime: number }) => void) => number; cancelVideoFrameCallback?: (h: number) => void }) | null;
    const onPresented = (now: number, meta: { mediaTime: number }) => {
      presented = { mediaTime: meta.mediaTime, at: now };
      if (withVfc?.requestVideoFrameCallback) vfc = withVfc.requestVideoFrameCallback(onPresented);
    };
    if (withVfc?.requestVideoFrameCallback) vfc = withVfc.requestVideoFrameCallback(onPresented);
    // A seek (the loop wrap, a moment click, the dwell engine) makes the cached presented frame
    // stale until the browser paints the frame at the seek target — and nothing is presented
    // while the seek is in flight. Reading the stale time at the span end re-issued the wrap seek
    // every tick, each cancelling the last, and the clip stuck at its end (user, 2026-09-19).
    // From the seek until the next presented frame the clock is currentTime, the seek target.
    const onSeeking = () => {
      presented = null;
    };
    video0?.addEventListener("seeking", onSeeking);
    const clock = (video: HTMLVideoElement) => {
      if (!presented || video.paused || video.seeking) return video.currentTime;
      const since = Math.max(0, Math.min(0.05, (performance.now() - presented.at) / 1000));
      return presented.mediaTime + since;
    };
    const draw = () => {
      raf = requestAnimationFrame(draw);
      const canvas = canvasRef.current;
      const video = videoRef.current;
      if (!canvas || !video) return;
      const t = clock(video);
      // Loop inside the analysed span: past its end (or before its start) jump back to the start.
      const spanEnd = spanEndFor(video.duration);
      if (Number.isFinite(spanEnd) && (t >= spanEnd - 0.02 || t < spanStart - 0.05)) {
        video.currentTime = spanStart;
        return;
      }
      // The panel (numbers, chart cursors) follows playback at the pose file's own rate — 10 Hz —
      // independent of whether the canvas can draw; the progress bar is a direct DOM write.
      if (progressRef.current && Number.isFinite(spanEnd) && spanEnd > spanStart)
        progressRef.current.style.width = `${(relTime(t) / (spanEnd - spanStart)) * 100}%`;
      if (tabRef.current === "metrics") setPanelTick(Math.floor(relTime(t) * PANEL_TICK_HZ));
      const ctx = canvas.getContext ? canvas.getContext("2d") : null;
      if (!ctx) return;
      // Loop wrap (the clip loops): every moment re-arms, and the backdrop follows.
      if (t < lastTimeRef.current - 0.5) consumedRef.current.clear();
      lastTimeRef.current = t;
      const bg = backdropRef.current;
      if (bg && Math.abs(bg.currentTime - t) > 0.2) bg.currentTime = t;
      const trailSeconds = strideSeconds(series) ?? TRAIL_SECONDS;
      const floatIdx = Math.min(frames.length - 1, Math.max(0, relTime(t) * fps));
      const idx = Math.floor(floatIdx);
      // The pose NOW: interpolated between the two pose frames around this instant. The trail,
      // its anchor and the skeleton all read this one sample, so nothing sits a whole pose frame
      // behind the clip (user, 2026-09-19: "the pose looks a bit behind the motion" — the trail's
      // head and anchor were the last whole frame, up to 100 ms behind the interpolated ankle).
      const pts = interpolatedFramePoints(frames, floatIdx);
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (isGait) {
        // The ankle trail is ONE tapered ribbon per foot, spanning ONE STRIDE.
        //
        // Both halves matter (user, 2026-09-19, on this upload: "17820 is a very strong cadence of
        // running", then "if its more consistent then it can be stronger"). At 204 spm the fixed
        // four-second window laid 6.7 strides of ankle path over each other, so the picture never
        // held a shape and the glow had to stay faint to avoid filling the frame. One stride draws
        // one loop, and a stride repeats, so the loop is the same every step. And the path is
        // filled once rather than stroked per frame: the per-segment halos used to stack into the
        // patchy look, where a single path takes a single even halo (and costs 0.04 ms a frame
        // against 1.93 ms, measured in Chromium).
        const trailFrames = Math.round(trailSeconds * fps);
        const first = Math.max(0, idx - trailFrames);
        // Anchored to the body (user, 2026-09-19): every past ankle point is drawn relative to where
        // the head is NOW, so a camera pan or shake moves the whole trail with the runner instead
        // of smearing it. The head (nose), else the hip midpoint; a frame without either is drawn
        // as-is.
        const anchorOf = (f: Map<string, { x: number; y: number }> | undefined) => {
          if (!f) return null;
          const nose = f.get("nose");
          if (nose) return nose;
          const lh = f.get("left_hip");
          const rh = f.get("right_hip");
          return lh && rh ? { x: (lh.x + rh.x) / 2, y: (lh.y + rh.y) / 2 } : null;
        };
        const anchorNow = anchorOf(pts);
        const relTo = (p: { x: number; y: number }, i: number) => {
          const a = anchorNow ? anchorOf(frames[i]) : null;
          return a && anchorNow ? { x: p.x - a.x + anchorNow.x, y: p.y - a.y + anchorNow.y } : p;
        };
        ctx.lineCap = "round";
        for (const [color, name] of [
          [TRAIL_LEFT, "left_ankle"],
          [TRAIL_RIGHT, "right_ankle"],
        ] as const) {
          // The whole frames up to this one, then the interpolated point as the head (already in
          // the current anchor's frame, so it is not re-anchored).
          const path: Array<{ x: number; y: number }> = [];
          for (let i = first; i <= idx; i++) {
            const raw = frames[i]?.get(name);
            if (raw) path.push(relTo(raw, i));
          }
          const head = pts.get(name);
          if (head && floatIdx > idx) path.push(head);
          const outline = ribbonOutline(path, TRAIL_HEAD_WIDTH * LOOK_STRENGTH, TRAIL_TAPER);
          if (outline.length === 0) continue;
          const tail = path[0];
          const tip = path[path.length - 1];
          const grad = ctx.createLinearGradient ? ctx.createLinearGradient(tail.x, tail.y, tip.x, tip.y) : null;
          if (grad) {
            grad.addColorStop(0, color + "00");
            grad.addColorStop(0.5, color + "77");
            grad.addColorStop(1, color);
          }
          ctx.shadowColor = color;
          ctx.shadowBlur = TRAIL_GLOW * LOOK_STRENGTH;
          ctx.fillStyle = grad ?? color;
          ctx.beginPath();
          ctx.moveTo(outline[0].x, outline[0].y);
          for (let i = 1; i < outline.length; i++) ctx.lineTo(outline[i].x, outline[i].y);
          ctx.closePath();
          ctx.fill();
          ctx.shadowBlur = 0;
        }
        // Foot strike: a ring in the foot's colour pulses out from where the ankle LANDED (the
        // strike frame's point, not the moving cursor) and fades within PULSE_SECONDS. Both
        // feet: the pass detects left and right strikes with the same rule.
        for (const [color, name, strikeIdx] of [
          [TRAIL_LEFT, "left_ankle", strikeIdxRef.current.left],
          [TRAIL_RIGHT, "right_ankle", strikeIdxRef.current.right],
        ] as const) {
          for (const { index, progress } of activeStrikePulses(strikeIdx, floatIdx, fps)) {
            // The pulse marks the CONTACT, so it is planted at the landing point for the whole
            // pulse — the lead-in included (user, 2026-09-20: "it shouldnt trace the foot, it
            // should just stay where it occurs"). It used to travel with the ankle until the
            // strike frame, which read as the mark chasing the foot rather than marking the spot.
            const at = frames[index]?.get(name);
            if (!at) continue;
            const ring = pulseRing(progress);
            ctx.strokeStyle = color;
            ctx.shadowColor = color;
            ctx.shadowBlur = 6;
            ctx.globalAlpha = ring.alpha;
            ctx.lineWidth = ring.width;
            ctx.beginPath();
            ctx.arc(at.x, at.y, ring.radius, 0, Math.PI * 2);
            ctx.stroke();
            // The centre: a solid dot in the foot's colour (user, 2026-09-19: the leg colours, not
            // white, and a larger pulse). It was briefly given a radial-gradient core and scaled up
            // with the ribbon; the owner preferred it as it was (2026-09-20: "i prefer the older
            // pulses"), so the strength dial moves the TRAIL only.
            ctx.shadowBlur = 0;
            ctx.globalAlpha = 1;
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.arc(at.x, at.y, PULSE_CORE_RADIUS, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      ctx.shadowBlur = 0;
      // The pose is coloured per side like the reference clip (user, 2026-09-19): left limbs in
      // the left colour, right limbs in the right one, the torso's cross-body bones neutral.
      ctx.lineWidth = 4;
      ctx.lineCap = "round";
      for (const [a, b] of SKELETON_BONES) {
        const pa = pts.get(a);
        const pb = pts.get(b);
        if (!pa || !pb) continue;
        ctx.strokeStyle = sideColor(a, b);
        ctx.beginPath();
        ctx.moveTo(pa.x, pa.y);
        ctx.lineTo(pb.x, pb.y);
        ctx.stroke();
      }
      pts.forEach((p, name) => {
        ctx.fillStyle = name.startsWith("left_") ? TRAIL_LEFT : name.startsWith("right_") ? TRAIL_RIGHT : JOINT;
        ctx.beginPath();
        ctx.arc(p.x, p.y, 4.5, 0, Math.PI * 2);
        ctx.fill();
      });
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      video0?.removeEventListener("seeking", onSeeking);
      if (vfc && withVfc?.cancelVideoFrameCallback) withVfc.cancelVideoFrameCallback(vfc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frames, fps, isGait, spanStart, splitWindow?.stopSec]);

  // Walk a position's cues one at a time, dwellMs each — the Pro video's rhythm — then play on. With no
  // cue rows the position's composite holds for one dwell, as before.
  const walkCues = (keys: string[], i: number) => {
    setActiveCue(keys[i] ?? null);
    dwellTimerRef.current = window.setTimeout(() => {
      if (i + 1 < keys.length) {
        walkCues(keys, i + 1);
        return;
      }
      dwellTimerRef.current = null;
      setDwelling(null);
      setActiveCue(null);
      videoRef.current?.play().catch(() => undefined);
    }, dwellMs);
  };
  const walkCuesRef = useRef(walkCues);
  walkCuesRef.current = walkCues;

  // The dwell timer belongs to the component, not to the effect below: that effect re-runs whenever
  // `dwelling` changes — which starting a dwell does — and its cleanup used to clear this timer, so a
  // dwell never resumed on its own.
  useEffect(() => () => {
    if (dwellTimerRef.current != null) window.clearTimeout(dwellTimerRef.current);
  }, []);

  // The dwell engine: crossing an unconsumed moment pauses the clip and walks its cues' stills;
  // after the last one the clip resumes on its own. "Play through" (the persisted preference)
  // skips the pause entirely.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || moments.length === 0 || !autoPause) return;
    const onTime = () => {
      if (dwelling) return;
      const t = video.currentTime;
      const next = moments.find(
        (m) => !consumedRef.current.has(m.label) && t >= m.timeSec && t < m.timeSec + 1.0
      );
      if (!next || !next.stillUrl) return;
      consumedRef.current.add(next.label);
      video.pause();
      setDwelling(next);
      walkCuesRef.current(cueKeysAtRef.current(next.label), 0);
    };
    video.addEventListener("timeupdate", onTime);
    const onSeeked = () => {
      // Re-arm every moment at or after the new position so a replayed section dwells again.
      const t = video.currentTime;
      for (const m of moments) if (m.timeSec >= t - 0.05) consumedRef.current.delete(m.label);
    };
    video.addEventListener("seeked", onSeeked);
    return () => {
      video.removeEventListener("timeupdate", onTime);
      video.removeEventListener("seeked", onSeeked);
    };
  }, [moments, dwelling, dwellMs, autoPause]);

  const toggleAutoPause = () => {
    const next = !autoPause;
    setAutoPause(next);
    try {
      window.localStorage.setItem(AUTO_PAUSE_KEY, next ? "on" : "off");
    } catch {
      /* preference only */
    }
    if (!next && dwelling) {
      if (dwellTimerRef.current != null) window.clearTimeout(dwellTimerRef.current);
      setDwelling(null);
      videoRef.current?.play().catch(() => undefined);
    }
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (dwelling) {
      if (dwellTimerRef.current != null) window.clearTimeout(dwellTimerRef.current);
      dwellTimerRef.current = null;
      setDwelling(null);
      setActiveCue(null);
      video.play().catch(() => undefined);
      return;
    }
    if (video.paused) video.play().catch(() => undefined);
    else video.pause();
  };

  const toggleZoom = () => {
    const next = !zoom;
    setZoom(next);
    try {
      window.localStorage.setItem(ZOOM_KEY, next ? "on" : "off");
    } catch {
      /* preference only */
    }
  };

  // Click-enabled stills: show a position's still at its moment and hold it until play.
  const showStill = (m: DwellMoment) => {
    const video = videoRef.current;
    if (!video) return;
    if (dwellTimerRef.current != null) window.clearTimeout(dwellTimerRef.current);
    dwellTimerRef.current = null;
    video.currentTime = m.timeSec;
    video.pause();
    consumedRef.current.add(m.label);
    setDwelling(m);
  };

  // One cue, on its own: hold the clip at its position and show that cue's still. Manual — it stops any
  // walk in progress and holds until Play, like a still.
  const showCue = (key: string | null) => {
    const row = key ? coachingRows.find((r) => r.key === key) : undefined;
    const m = row ? moments.find((x) => x.label === row.pLabel) : undefined;
    if (!row || !m) return;
    const video = videoRef.current;
    if (!video) return;
    if (dwellTimerRef.current != null) window.clearTimeout(dwellTimerRef.current);
    dwellTimerRef.current = null;
    video.currentTime = m.timeSec;
    video.pause();
    consumedRef.current.add(m.label);
    setDwelling(m);
    setActiveCue(row.key);
  };

  // A position chip opens that position at its first cue; with no cue rows, its composite as before.
  const openPosition = (m: DwellMoment) => {
    const first = cueKeysAt(m.label)[0];
    if (first) showCue(first);
    else if (m.stillUrl) showStill(m);
    else seekTo(m);
  };

  const seekTo = (m: DwellMoment) => {
    const video = videoRef.current;
    if (!video) return;
    if (dwellTimerRef.current != null) window.clearTimeout(dwellTimerRef.current);
    dwellTimerRef.current = null;
    setDwelling(null);
    video.currentTime = Math.max(0, m.timeSec - 0.6);
    video.play().catch(() => undefined);
  };

  // Arrow keys step cue by cue, like the Next / Previous buttons — never while the user is typing.
  const showCueRef = useRef(showCue);
  showCueRef.current = showCue;
  useEffect(() => {
    if (coachingRows.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName))) return;
      e.preventDefault();
      showCueRef.current(stepCue(coachingRows, activeCueRef.current, e.key === "ArrowRight" ? 1 : -1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [coachingRows]);

  // The current pose file frame — the cursor every chart beside the player follows.
  const cursorFrame = useMemo(() => {
    void panelTick;
    return frameIndexAt(relTime(videoRef.current?.currentTime ?? 0), fps, frames.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelTick, fps, frames.length]);

  // Every number below is the C# analysis pass's (liveSeries) — the same statics the rendered
  // pills print. The browser renders: charts in the page's own chart shape, tiles that read the
  // artifact's sample at the cursor. No artifact (404) = no panels, never a local recompute.
  const gaitPanel = useMemo(() => {
    if (!series || !isGait) return null;
    void panelTick;
    const charts: MetricChartData[] = [];
    if (series.cadenceSeries.length >= 2)
      charts.push(seriesChart("Cadence over time", "spm", series.cadenceSeries, series.minFrame, (x) => x.spm));
    if (series.kneeAtStrike && series.kneeAtStrike.perStrike.length >= 2)
      charts.push(
        seriesChart("R knee at foot strike", "°", series.kneeAtStrike.perStrike, series.minFrame, (x) => x.deg, "positionMeasures")
      );
    // The cadence reading at the cursor: the running cadence the pass computed at the last strike
    // reached so far; the whole-clip number before the first one.
    const idx = frameIndexAt(relTime(videoRef.current?.currentTime ?? 0), fps, frames.length);
    const started = idx > 0;
    let spm: number | null = series.cadenceSpm;
    if (started) {
      const reached = series.cadenceSeries.filter((x) => x.frame - series.minFrame <= idx);
      spm = reached.length > 0 ? reached[reached.length - 1].spm : null;
    }
    return { spm, strikes: series.strikes.length, knee: series.kneeAtStrike, charts };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, isGait, panelTick, fps, frames.length]);

  // Body joint angles per side and joint, straight from the artifact (the reference product's
  // "Body Joint Angles": one big chart with a series picker, tiles per side that read at the cursor).
  const JOINT_LABELS: Record<string, string> = { knee: "Knee angle", hip: "Hip angle", elbow: "Elbow angle", shoulder: "Shoulder angle" };
  const joints = useMemo(() => {
    if (!series) return [] as Array<{ key: string; label: string; left: AngleSampleList; right: AngleSampleList }>;
    return Object.keys(JOINT_LABELS)
      .map((key) => ({
        key,
        label: JOINT_LABELS[key],
        left: series.jointAngles?.left?.[key] ?? [],
        right: series.jointAngles?.right?.[key] ?? [],
      }))
      .filter((j) => j.left.length > 0 || j.right.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series]);

  // The series picker: gait's own series first for gait, then the body joints, then the page's
  // charts. Values are stable keys so a re-render never loses the choice.
  const seriesOptions = useMemo(() => {
    const out: Array<{ value: string; label: string }> = [];
    (gaitPanel?.charts ?? []).forEach((c, i) => out.push({ value: `gait:${i}`, label: c.title }));
    joints.forEach((j) => out.push({ value: `joint:${j.key}`, label: j.label }));
    pageCharts.forEach((c, i) => out.push({ value: `page:${i}`, label: c.title }));
    return out;
  }, [gaitPanel?.charts, joints, pageCharts]);
  const activeSeries =
    seriesPick && seriesOptions.some((o) => o.value === seriesPick) ? seriesPick : seriesOptions[0]?.value ?? null;

  const bigChart = (() => {
    if (!activeSeries) return null;
    const [kind, id] = activeSeries.split(":");
    if (kind === "gait") {
      const c = gaitPanel?.charts[Number(id)];
      return c ? <LiveAngleChart chart={c} index={0} cursorFrame={cursorFrame} extremes xTime={fps} tall /> : null;
    }
    if (kind === "page") {
      const c = pageCharts[Number(id)];
      return c ? <LiveAngleChart chart={c} index={1} cursorFrame={cursorFrame} extremes xTime={fps} tall /> : null;
    }
    const j = joints.find((x) => x.key === id);
    if (!j || !series) return null;
    const short = j.label.replace(/ angle$/i, "").toLowerCase();
    const primary = j.left.length > 0 ? { side: "Left", color: TRAIL_LEFT, samples: j.left } : { side: "Right", color: TRAIL_RIGHT, samples: j.right };
    const chart = seriesChart(j.label, "°", primary.samples, series.minFrame, (x) => x.deg);
    const other =
      j.left.length > 0 && j.right.length > 0
        ? [{ label: `Right ${short}`, color: TRAIL_RIGHT, dataPoints: seriesChart("", "°", j.right, series.minFrame, (x) => x.deg).dataPoints }]
        : [];
    return (
      <LiveAngleChart
        chart={chart}
        primaryLabel={`${primary.side} ${short}`}
        color={primary.color}
        extra={other}
        cursorFrame={cursorFrame}
        extremes
        xTime={fps}
        tall
      />
    );
  })();

  // Where the metrics go follows the canvas: a portrait canvas (gait) is a narrow column with
  // room beside it from md up; a landscape canvas (golf) wants the width, so the column stacks
  // underneath — except on large screens, where the live card spans the whole page row and a
  // column beside the stage leaves the stage bigger than stacking does.
  const landscape = stage.w > stage.h;

  return (
    <div
      className={`flex-1 min-h-0 flex bg-black ${landscape ? "flex-col lg:flex-row" : "flex-col md:flex-row"}`}
      data-testid="live-free-analysis"
    >
      <div className="relative flex-1 min-h-0">
        {/* The 16:9 STAGE — the stills' canvas shape. Playback lives inside it with a blurred
            cover backdrop and the renderer's watermark, so a still fading in is the SAME
            surface getting feedback burnt in, not a different-looking view (user review,
            2026-09-12: "the watermarks and sizing differ from the playback view"). */}
        <div ref={containerRef} className="absolute inset-x-0 top-0 bottom-11 flex items-center justify-center overflow-hidden bg-black">
          {/* The stage is a VISIBLE, constant column in both states: the live backdrop is clipped
              to it exactly as the still's baked backdrop is, so when the still fades in only the
              column's CONTENTS change — its edges, size and position never move. (Measured on
              17816: a panel-wide backdrop made the column invisible during playback and then
              snap in as a hard-edged card with the still.) */}
          <div
            className="relative overflow-hidden bg-black"
            style={stageBox ? { width: stageBox.w, height: stageBox.h } : { visibility: "hidden", width: 1, height: 1 }}
            onClick={togglePlay}
            data-testid="live-stage"
          >
            <video
              ref={backdropRef}
              src={clipUrl}
              className="absolute inset-0 w-full h-full object-cover blur-xl brightness-[.55] scale-110"
              muted
              playsInline
              loop
              preload="auto"
              aria-hidden="true"
              data-testid="live-backdrop"
            />
            {/* The sharp window at the renderer's placement; the clip and the skeleton canvas
                share one rect inside it, scaled so the renderer's crop fills the window. */}
            <div className="absolute overflow-hidden" style={windowStyle} data-testid="live-placement">
              {/* max-w-none: Tailwind's preflight caps <video> at max-width:100%, which would pin
                  a crop-scaled clip to the window width while the canvas scales — measured on
                  17816 with a synthetic stage. */}
              <video
                ref={videoRef}
                src={clipUrl}
                className="absolute max-w-none"
                style={clipStyle}
                autoPlay
                muted
                playsInline
                loop
                preload="auto"
                onPlay={() => {
                  setPlaying(true);
                  backdropRef.current?.play().catch(() => undefined);
                }}
                onPause={() => {
                  setPlaying(false);
                  backdropRef.current?.pause();
                }}
                onEnded={() => setPlaying(false)}
                onLoadedMetadata={(e) => {
                  if (spanStart > 0 && e.currentTarget.currentTime < spanStart) e.currentTarget.currentTime = spanStart;
                }}
                data-testid="live-clip"
              />
              <canvas
                ref={canvasRef}
                width={poseFile.captureWidth || 720}
                height={poseFile.captureHeight || 1280}
                className="absolute pointer-events-none max-w-none"
                style={clipStyle}
                data-testid="live-skeleton-canvas"
              />
            </div>
            {!dwelling && <RendererWatermark stamp={watermarkStamp} w={stage.w} h={stage.h} />}
            {dwelling && stageStillUrl && (
              <img
                src={`${stillBase}${stageStillUrl}`}
                alt={activeRowData ? `${dwelling.label} · ${activeRowData.description}` : `${dwelling.label} feedback`}
                className="absolute inset-0 w-full h-full object-contain bg-black"
                data-testid="live-dwell-still"
              />
            )}
            {showingAllCues && (
              <div
                className="absolute top-2 right-2 rounded bg-black/70 px-2 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.06em] text-white/85"
                data-testid="live-still-all-cues"
                title="This upload was analysed before each cue had its own picture — re-render it to split them"
              >
                All cues at {dwelling!.label}
              </div>
            )}
          </div>
        </div>
        {/* No centred play badge while paused (user, 2026-09-19: "it blocks the view") — it sat
            dead over the athlete, the one thing the view exists to show. Resuming never depended
            on it: the stage toggles play on click and the control bar below carries Play/Pause. */}
        <div className="absolute bottom-0 inset-x-0">
          <div className="h-1 bg-white/20">
            <div ref={progressRef} className="h-1 bg-el-500" style={{ width: "0%" }} />
          </div>
          <div className="flex items-center gap-1 overflow-x-auto px-2 py-2 bg-black/60">
            <button
              type="button"
              onClick={togglePlay}
              aria-label={playing ? "Pause" : "Play"}
              className="shrink-0 h-6 w-6 rounded bg-white/10 text-white/90 flex items-center justify-center hover:bg-white/20"
            >
              {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
            </button>
            <button
              type="button"
              onClick={() => {
                const video = videoRef.current;
                if (!video) return;
                consumedRef.current.clear();
                video.currentTime = spanStart;
                video.play().catch(() => undefined);
              }}
              aria-label="Replay"
              className="shrink-0 h-6 w-6 rounded bg-white/10 text-white/90 flex items-center justify-center hover:bg-white/20"
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
            {coachingRows.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={() => showCue(stepCue(coachingRows, activeCue, -1))}
                  aria-label="Previous cue"
                  className="shrink-0 h-6 w-6 rounded bg-white/10 text-white/90 flex items-center justify-center hover:bg-white/20"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => showCue(stepCue(coachingRows, activeCue, 1))}
                  aria-label="Next cue"
                  className="shrink-0 h-6 w-6 rounded bg-white/10 text-white/90 flex items-center justify-center hover:bg-white/20"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </>
            )}
            {moments.map((m) => (
              <button
                key={m.label}
                type="button"
                onClick={() => openPosition(m)}
                className={`px-2 py-1 rounded text-[0.65rem] uppercase tracking-[0.06em] font-semibold transition shrink-0 ${
                  dwelling?.label === m.label
                    ? "bg-el-500 text-white"
                    : "bg-white/10 text-white/80 hover:bg-white/20"
                }`}
              >
                {m.label}
              </button>
            ))}
            {clientCrop && (
              <button
                type="button"
                onClick={toggleZoom}
                data-testid="live-zoom-toggle"
                className={`ml-auto shrink-0 px-2 py-1 rounded text-[0.6rem] uppercase tracking-[0.06em] font-semibold transition ${
                  zoom ? "bg-el-500/20 text-el-300" : "bg-white/10 text-white/60 hover:bg-white/20"
                }`}
                title="Frame the athlete from the keypoints, or show the full frame"
              >
                {zoom ? "Athlete zoom" : "Full frame"}
              </button>
            )}
            <button
              type="button"
              onClick={toggleAutoPause}
              data-testid="live-autopause-toggle"
              className={`${clientCrop ? "" : "ml-auto "}shrink-0 px-2 py-1 rounded text-[0.6rem] uppercase tracking-[0.06em] font-semibold transition ${
                autoPause ? "bg-el-500/20 text-el-300" : "bg-white/10 text-white/60 hover:bg-white/20"
              }`}
              title="Pause on each position to show its feedback, or play straight through"
            >
              {autoPause ? "Pauses on feedback" : "Plays through"}
            </button>
          </div>
        </div>
      </div>

      {/* The metrics column: the SAME white chart cards the session page renders at the
          bottom, on the platform's light surface — NOT a custom dark panel "inside the video"
          (user review, 2026-09-12). Structure borrowed from the reference product (user,
          2026-09-12: "learn from their design from a ux perspective" — our colours stay): one
          big chart with a series picker, then tiles that read at the cursor. */}
      {/* Always present: it holds the tabs, and Metrics says so when the analysis pass has not
          produced the artifact yet. */}
      {(
        <div
          className={
            landscape
              ? "shrink-0 w-full max-h-[48%] lg:max-h-none lg:w-[38%] lg:max-w-[560px] bg-slate-50 border-t lg:border-t-0 lg:border-l border-slate-200 p-3 flex flex-col gap-3 overflow-auto"
              : "shrink-0 w-full max-h-[45%] md:max-h-none md:w-1/2 md:max-w-[600px] bg-slate-50 border-t md:border-t-0 md:border-l border-slate-200 p-3 flex flex-col gap-3 overflow-auto"
          }
          data-testid="live-side-charts"
          data-cursor-frame={cursorFrame}
        >
          <div className="flex items-center justify-between gap-2" data-testid="live-tabs-row">
            <div className="inline-flex rounded-md bg-slate-100 p-0.5" role="tablist">
              {(["coaching", "metrics"] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={tab === t}
                  onClick={() => setPickedTab(t)}
                  data-testid={`live-tab-${t}`}
                  className={`px-3 py-1 text-xs font-medium rounded transition ${
                    tab === t ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  {t === "coaching" ? "Coaching" : "Metrics"}
                </button>
              ))}
            </div>
          </div>
          {tab === "metrics" && seriesOptions.length > 0 && (
            <label className="flex items-center gap-2 text-[0.6rem] uppercase tracking-[0.14em] text-slate-500">
              Series
              <select
                value={activeSeries ?? ""}
                onChange={(e) => setSeriesPick(e.target.value)}
                className="flex-1 min-w-0 normal-case tracking-normal text-xs text-slate-800 bg-white border border-slate-200 rounded px-2 py-1"
                aria-label="Series"
                data-testid="live-series-picker"
              >
                {seriesOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          {tab === "coaching" && (
            <>
              {(coaching?.summary || coaching?.total) && (
                <div className="rounded-md bg-white border border-slate-100 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="h-4 w-4 text-el-600" />
                    <div className="text-sm font-semibold text-slate-900">Summary</div>
                    {coaching?.total && (
                      <span className="ml-auto text-xs font-semibold text-el-700">Total {coaching.total}</span>
                    )}
                  </div>
                  {coaching?.summary && <p className="text-xs leading-relaxed text-slate-600">{coaching.summary}</p>}
                </div>
              )}
              {coachingRows.length > 0 ? (
                <ul className="space-y-2" data-testid="live-coaching-list">
                  {coachingRows.map((r) => {
                    // ONE cue lights up — the one on the stage — never every row of its position.
                    const isActive = activeCue === r.key;
                    return (
                      <li key={r.key}>
                        <button
                          type="button"
                          data-testid="live-coaching-row"
                          data-active={isActive ? "true" : "false"}
                          onClick={() => showCue(r.key)}
                          className={`w-full text-left rounded-md transition px-3 py-2 flex items-baseline justify-between gap-3 border ${
                            isActive
                              ? "bg-el-50 border-el-300 ring-1 ring-el-200"
                              : "bg-white border-slate-100 hover:border-el-200"
                          }`}
                        >
                          <div className="flex items-baseline gap-2 min-w-0">
                            <span className="text-[0.65rem] font-semibold text-el-700 shrink-0 tabular-nums">
                              {r.pLabel}
                            </span>
                            <div className="min-w-0">
                              <div className={`text-xs truncate ${isActive ? "font-semibold text-el-800" : "font-medium text-slate-800"}`}>
                                {r.description}
                              </div>
                              {r.subText && <div className="text-[0.6rem] text-slate-400 truncate">{r.subText}</div>}
                            </div>
                          </div>
                          {r.values && r.values.length > 0 && (
                            <div className="flex shrink-0 gap-1">
                              {r.values.map((v) => (
                                <span
                                  key={v}
                                  className={`rounded px-1.5 py-0.5 text-[0.6rem] font-semibold tabular-nums ${
                                    isActive ? "bg-el-100 text-el-800" : "bg-slate-100 text-slate-600"
                                  }`}
                                >
                                  {v}
                                </span>
                              ))}
                            </div>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="text-xs text-slate-500">No coaching feedback for this clip yet.</div>
              )}
            </>
          )}

          {tab === "metrics" && (
            <>
          {!liveSeries && (
            <div className="rounded-md bg-white border border-slate-100 p-3 text-xs text-slate-500" data-testid="live-metrics-pending">
              Live metrics are produced by the analysis pass and are not available for this clip yet.
            </div>
          )}
          {liveSeries && !series && (
            <div className="rounded-md bg-white border border-slate-100 p-3 text-xs text-slate-500" data-testid="live-metrics-stale">
              Live metrics for this clip were computed at a different frame rate and need re-running by the analysis pass.
            </div>
          )}
          {bigChart}

          {gaitPanel && (
            <div data-testid="live-gait-panel">
              <div className="text-[0.6rem] uppercase tracking-[0.14em] text-slate-500 mb-1">Gait</div>
              <div className="grid gap-2 grid-cols-3">
                <Tile
                  value={gaitPanel.spm != null ? `${Math.round(gaitPanel.spm)} spm` : "—"}
                  label="Cadence"
                  testid="live-cadence-spm"
                />
                <Tile value={`${gaitPanel.strikes}`} label="Strikes" />
                <Tile
                  value={gaitPanel.knee ? `${Math.round(gaitPanel.knee.meanDeg)}° ±${Math.round(gaitPanel.knee.spreadDeg)}°` : "—"}
                  label="R knee @ strike"
                  testid="live-knee-at-strike"
                />
              </div>
              <div className="text-[0.65rem] text-slate-400 mt-1">
                <span style={{ color: TRAIL_LEFT }}>●</span> left ·{" "}
                <span style={{ color: TRAIL_RIGHT }}>●</span> right ankle path
              </div>
            </div>
          )}

          {joints.length > 0 &&
            (["right", "left"] as const).map((side) => (
              <div key={side}>
                <div className="text-[0.6rem] uppercase tracking-[0.14em] text-slate-500 mb-1">
                  <span style={{ color: side === "left" ? TRAIL_LEFT : TRAIL_RIGHT }}>●</span> {side} body
                </div>
                <div className="grid gap-2 grid-cols-2">
                  {joints.map((j) => {
                    const sample = series ? sampleAtIndex(j[side], series.minFrame, cursorFrame) : null;
                    return (
                      <Tile
                        key={j.key}
                        value={sample ? `${Math.round(sample.deg)}°` : "—"}
                        label={j.label}
                        testid={`live-angle-tile-${side}-${j.key}`}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
